// Dependencies
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { faArrowLeft, faPlus, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

//components
import { AdminTitle } from "../../../../../../../components/Admin";
import Button from "../../../../../../../components/Button";
import TextInput from "../../../../../../../components/Inputs/InputText";
import Link from "../../../../../../../components/Link";
import Spacer from "../../../../../../../components/Spacer";
import Text from "../../../../../../../components/Text";

import useDebouncedValue from "../../../../../../../hooks/useDebouncedValue";
import useNavigate from "../../../../../../../hooks/useNavigate";
// Hooks
import useToast from "../../../../../../../hooks/useToast";

// API
import {
	createUserSelectionFromCohort,
	fetchNewSelectionObject,
	updateCohort,
} from "../../../../../../../api/services";

// Selectors
import { getURLParam } from "../../../../../../../selectors/navigation";

import get from "../../../../../../../utils/GetNested";
// Utils
import pluralizer from "../../../../../../../utils/pluralizer";

// Components
import Directory from "../../../../Directory";
// Styles
import css from "./StaticEmployees.module.scss";

const AddEmployeesColumns = [
	{
		id: "Group",
		label: "PY Group",
		isSortable: true,
		getter: (row) => {
			return get(row, ["data_fields", "Group", "value", "name"], "");
		},
	},
	{
		id: "Manager",
		label: "Manager",
		isSortable: true,
		getter: (row) => {
			return get(row, ["data_fields", "Manager", "value", "name"], "");
		},
	},
];

declare interface AddEmployeesDirectoryProps {
	backUrl: string;
	cohortName: string;
}

/**
 * In order to add a user to a cohort, we need to display a list of
 * employees that are currently not in the cohort. We do this by creating a
 * user selection object from the cohort and use it as the exclusion list for
 * the directory api. Now that we have a list of users that are not in the the
 * cohort, we can add them to a new user selection object -- called the
 * "staging" selection. When the user is done, we add all users in the new
 * selection object to the cohort.
 * @param props
 * @returns
 */
const AddEmployeesDirectory = ({ backUrl, cohortName }: AddEmployeesDirectoryProps) => {
	const toast = useToast();
	const navigate = useNavigate();
	const cohortID = useSelector((state) => getURLParam(state, "cohortID"));

	const [search, setSearch] = useState("");
	const [isUpdating, setIsUpdating] = useState(false);
	const debouncedSearch = useDebouncedValue(search, 250);
	const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
	const [userSelectionIDs, setUserSelectionIDs] = useState<{ current: string; staging: string } | null>(null);

	useEffect(() => {
		const fetchCohortUserSelection = async () => {
			if (cohortID) {
				const [exclusionSelection, staging] = await Promise.all([
					createUserSelectionFromCohort(cohortID),
					fetchNewSelectionObject(),
				]);

				setUserSelectionIDs({
					current: exclusionSelection.user_selection,
					staging: staging.user_selection_id,
				});
			}
		};

		fetchCohortUserSelection();
	}, [cohortID]);

	const handleAddEmployees = async () => {
		setIsUpdating(true);
		try {
			await updateCohort(cohortID, {
				addUserSelectionID: userSelectionIDs?.staging,
			});
			toast(
				`${selectedEmployees.length} ${pluralizer(
					selectedEmployees.length,
					"Employee has",
					"Employees have"
				)} been added.`,
				{ type: "success", size: "large" }
			);
			navigate(`/administration/engagement/cohorts/${cohortID}/`);
		} catch (e) {
			toast("Unable to add employees to cohort.", { type: "error", size: "large" });
		} finally {
			setIsUpdating(false);
		}
	};

	const isLoading = userSelectionIDs === null || isUpdating;

	return (
		<>
			<AdminTitle includeSpacer={false}>Employee Cohorts</AdminTitle>
			<Spacer height={4} />
			<div className={css.main}>
				<div className={css.detailsContainer}>
					<div className={`${css.backButtonContainer} ${css.addEmployeesAdjustment}`}>
						<Link overrideStyles={true} href={backUrl} className={css.backButton}>
							<FontAwesomeIcon icon={faArrowLeft} />
							<Text size="paragraph-3" weight="semi-bold">
								Back
							</Text>
						</Link>
					</div>
					<div className={css.info}>
						<div className={css.nameContainer}>
							<Text className={css.name} size="paragraph-1" weight="semi-bold" color="neutral-9">
								Add Members to {cohortName}
							</Text>
						</div>
					</div>
				</div>

				<Spacer height={7} />
				<div className={css.directoryWrapper}>
					<div className={css.controls}>
						<div className={css.search}>
							<TextInput
								icon={faSearch}
								value={search}
								onChange={setSearch}
								placeholder="Search Employees..."
								clearable
							/>
						</div>
						<div />
						<div>
							<Button
								isRequesting={isUpdating}
								onClick={handleAddEmployees}
								color="green"
								icon={faPlus}
								iconPosition="left"
								disabled={selectedEmployees.length <= 0}
							>
								Add to Cohort
							</Button>
						</div>
					</div>

					<Spacer height={2} />
					<Directory
						columns={AddEmployeesColumns}
						stagingUserSelectionID={userSelectionIDs?.staging}
						selectedEmployees={selectedEmployees}
						setSelectedEmployees={setSelectedEmployees}
						employeeSearchProps={{
							searchTerm: debouncedSearch,
							limitSelected: false,
							userSelectionID: userSelectionIDs?.current,
							exclusionListId: userSelectionIDs?.current,
						}}
						canBulkSelect
						isLoading={isLoading}
						emptyText={
							debouncedSearch
								? "A search on the employee list results in no matches."
								: "No employees to display."
						}
					/>
				</div>
			</div>
		</>
	);
};

export default AddEmployeesDirectory;
